/**
 * Calendrier des absences — vue mensuelle simple.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Download } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const monthLabel = (d) => d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

function getMonthGrid(year, month) {
  // Lundi-Dimanche
  const first = new Date(year, month, 1);
  const startDow = (first.getDay() + 6) % 7; // 0=Mon
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const toISO = (d) => d.toISOString().slice(0, 10);

export default function AbsencesCalendarPage() {
  const [cursor, setCursor] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [types, setTypes] = useState([]);
  const [filterDept, setFilterDept] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('date_from', toISO(monthStart));
      params.append('date_to', toISO(monthEnd));
      if (filterDept !== 'all') params.append('department_id', filterDept);
      if (filterType !== 'all') params.append('leave_type_id', filterType);
      const [calRes, deptRes, typesRes] = await Promise.all([
        api.get(`/leave/calendar?${params}`),
        api.get('/departments').catch(() => ({ data: [] })),
        api.get('/leave/types').catch(() => ({ data: { items: [] } })),
      ]);
      setEvents(calRes.data?.events || []);
      setHolidays(calRes.data?.holidays || []);
      setDepartments(deptRes.data?.departments || deptRes.data || []);
      setTypes(typesRes.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  }, [cursor, filterDept, filterType]); // eslint-disable-line

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const grid = useMemo(() => getMonthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const holidaysSet = useMemo(() => new Set(holidays.map(h => h.date)), [holidays]);

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach(e => {
      const s = new Date(e.startDate);
      const en = new Date(e.endDate);
      for (let d = new Date(s); d <= en; d.setDate(d.getDate() + 1)) {
        const k = toISO(d);
        if (!map[k]) map[k] = [];
        map[k].push(e);
      }
    });
    return map;
  }, [events]);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="absences-calendar-page">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <CalendarIcon className="w-6 h-6 text-indigo-600" />
              Calendrier des absences
            </h1>
            <p className="text-sm text-slate-500 mt-1">Vue mensuelle des absences approuvées et en attente.</p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                const r = await api.get('/leave/calendar.ics?scope=mine', { responseType: 'blob' });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement('a');
                a.href = url; a.download = 'absences-hr-engine.ics'; a.click();
                URL.revokeObjectURL(url);
                toast.success('Calendrier exporté — importez le fichier dans Outlook ou Google Calendar');
              } catch (e) { toast.error('Erreur export'); }
            }} data-testid="ics-export-btn">
              <Download className="w-4 h-4 mr-2" />Exporter (.ics)
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-semibold text-sm w-40 text-center capitalize">{monthLabel(cursor)}</span>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Aujourd'hui</Button>
          </div>
        </div>

        <div className="flex gap-3 mb-4 flex-wrap">
          <Select value={filterDept} onValueChange={setFilterDept}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Département" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous départements</SelectItem>
              {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous types</SelectItem>
              {types.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? <p className="text-sm text-slate-400 text-center py-8">Chargement...</p> : (
          <Card>
            <CardContent className="p-3">
              <div className="grid grid-cols-7 gap-1 text-xs">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
                  <div key={d} className="text-center font-semibold py-2 text-slate-600">{d}</div>
                ))}
                {grid.map((d, i) => {
                  if (!d) return <div key={i} className="bg-slate-50 rounded h-24" />;
                  const k = toISO(d);
                  const events_ = eventsByDay[k] || [];
                  const isHoliday = holidaysSet.has(k);
                  const isToday = toISO(new Date()) === k;
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <div key={i} className={`rounded h-24 p-1 border ${isToday ? 'border-indigo-400' : 'border-slate-100'} ${isHoliday ? 'bg-amber-50' : isWeekend ? 'bg-slate-50' : 'bg-white'}`}
                      data-testid={`cal-day-${k}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-semibold ${isToday ? 'text-indigo-600' : 'text-slate-700'}`}>{d.getDate()}</span>
                        {isHoliday && <span className="text-[10px] text-amber-700">Férié</span>}
                      </div>
                      <div className="space-y-0.5 mt-1 overflow-hidden">
                        {events_.slice(0, 3).map((e, idx) => (
                          <div key={idx} className="text-[10px] px-1 rounded truncate flex items-center gap-1"
                            style={{ backgroundColor: (e.leaveType?.color || '#94a3b8') + '20', color: e.leaveType?.color || '#475569' }}
                            title={`${e.employee?.firstName} ${e.employee?.lastName} — ${e.leaveType?.name}`}>
                            <span>{e.employee?.firstName?.[0]}{e.employee?.lastName?.[0]}</span>
                            <span className="truncate">{e.leaveType?.name}</span>
                          </div>
                        ))}
                        {events_.length > 3 && (
                          <div className="text-[10px] text-slate-500 px-1">+ {events_.length - 3} autre(s)</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-3 mt-3 text-xs text-slate-500 flex-wrap">
                <span>Légende :</span>
                {types.slice(0, 6).map(t => (
                  <div key={t.id} className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: t.color }} />
                    <span>{t.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
